/* avelore-datalayer.js
 * -----------------------------------------------------------------------------
 * dataLayer da vitrine Avelore (tema Shopify) para o GTM-56XZWTV7:
 *   - view_item / view_item_list / view_cart a partir do JSON da página
 *     (#avelore-page-data, gerado por snippets/avelore-datalayer.liquid);
 *   - add_to_cart / remove_from_cart observando as alterações do carrinho;
 *   - super_view_content (15 s) e hiper_view_content (30 s) de página de
 *     produto VISÍVEL, com event_id e o payload da Meta (trackSingleCustom no GTM).
 * Blocos entre marcadores: cópias exatas de rastreamento/tema/src/*.mjs.
 * -----------------------------------------------------------------------------
 */
(function (win, doc) {
"use strict";
if (!win || !doc || win.__aveloreDataLayerLoaded) return;
win.__aveloreDataLayerLoaded = true;

// Formato dos content_ids da Meta: "product_id", "variant_id" ou "shopify_catalog" -
// deve ser o mesmo que o app Facebook & Instagram usa. Conferido na prévia em
// 23/09/2026: o app envia o ID do produto com content_type "product_group".
var META_CONTENT_ID_FORMAT = "product_id";
var ENGAGEMENT_THRESHOLDS_MS = [15000, 30000];

// <mapping>
var ECOMMERCE_LIST_LIMIT = 50;

var ENGAGEMENT_EVENTS = {
  15: { ga4: "super_view_content", meta: "SuperViewContent" },
  30: { ga4: "hiper_view_content", meta: "HiperViewContent" }
};

// Preços do Liquid e de /cart.js vêm em centavos (inteiros).
function centsToAmount(cents) {
  var n = typeof cents === "string" ? parseFloat(cents) : cents;
  if (typeof n !== "number" || !isFinite(n)) return 0;
  return Math.round(n) / 100;
}

function roundMoney(value) {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

// A variante única da Shopify se chama "Default Title": não é informação útil.
function cleanVariantTitle(title) {
  return title && title !== "Default Title" ? String(title) : "";
}

// Mesmo critério do pixel do checkout: SKU quando existe, senão ID da variante.
function itemIdFrom(sku, variantId) {
  if (sku) return String(sku);
  return variantId != null ? String(variantId) : "";
}

function buildItem(product, variant, extra) {
  product = product || {};
  variant = variant || {};
  extra = extra || {};
  var item = {
    item_id: itemIdFrom(variant.sku, variant.id),
    item_name: product.title || "",
    item_variant: cleanVariantTitle(variant.title),
    price: centsToAmount(variant.price),
    quantity: extra.quantity != null ? extra.quantity : 1
  };
  if (product.vendor) item.item_brand = product.vendor;
  if (product.type) item.item_category = product.type;
  if (extra.index != null) item.index = extra.index;
  if (extra.listId) item.item_list_id = extra.listId;
  if (extra.listName) item.item_list_name = extra.listName;
  return item;
}

// Linha de carrinho (mesmos nomes de campo do /cart.js e do JSON do tema).
// `price` = preço unitário final (após descontos automáticos da linha).
function buildCartLineItem(line, quantity) {
  line = line || {};
  var item = {
    item_id: itemIdFrom(line.sku, line.variant_id),
    item_name: line.product_title || "",
    item_variant: cleanVariantTitle(line.variant_title),
    price: centsToAmount(line.final_price),
    quantity: quantity != null ? quantity : (line.quantity || 0)
  };
  if (line.vendor) item.item_brand = line.vendor;
  if (line.product_type) item.item_category = line.product_type;
  return item;
}

function findVariant(product, variantId) {
  var variants = product && Array.isArray(product.variants) ? product.variants : [];
  if (variantId == null) return null;
  for (var i = 0; i < variants.length; i++) {
    if (variants[i] && String(variants[i].id) === String(variantId)) return variants[i];
  }
  return null;
}

// Prioridade: variante da URL -> variante selecionada na carga -> primeira.
function selectedVariant(product, variantId) {
  if (!product) return null;
  var byUrl = findVariant(product, variantId);
  if (byUrl) return byUrl;
  var selected = findVariant(product, product.selected_variant_id);
  if (selected) return selected;
  return Array.isArray(product.variants) && product.variants.length ? product.variants[0] : null;
}

function variantIdFromSearch(search) {
  var match = /[?&]variant=(\d+)/.exec(String(search || ""));
  return match ? match[1] : null;
}

function buildViewItemEvent(pageData, variantId) {
  var product = pageData && pageData.product;
  var variant = selectedVariant(product, variantId);
  if (!product || !variant) return null;
  var item = buildItem(product, variant, { quantity: 1 });
  return { event: "view_item", ecommerce: { currency: pageData.currency, value: item.price, items: [item] } };
}

function buildViewItemListEvent(pageData) {
  var list = pageData && pageData.list;
  var products = list && Array.isArray(list.products) ? list.products.slice(0, ECOMMERCE_LIST_LIMIT) : [];
  if (!products.length) return null;
  var items = products.map(function (p, index) {
    return buildItem(p, p && p.variant, { quantity: 1, index: index, listId: list.id, listName: list.name });
  });
  return { event: "view_item_list", ecommerce: { currency: pageData.currency, item_list_id: list.id, item_list_name: list.name, items: items } };
}

function sumItemsValue(items) {
  return roundMoney(items.reduce(function (sum, item) { return sum + item.price * item.quantity; }, 0));
}

function buildViewCartEvent(pageData) {
  var cart = (pageData && pageData.cart) || {};
  var lines = Array.isArray(cart.items) ? cart.items : [];
  return {
    event: "view_cart",
    ecommerce: {
      currency: pageData && pageData.currency,
      value: centsToAmount(cart.total_price),
      items: lines.map(function (l) { return buildCartLineItem(l); })
    }
  };
}

// Soma quantidades por variante (a mesma variante pode ocupar várias linhas).
function aggregateByVariant(cart) {
  var map = {};
  var lines = cart && Array.isArray(cart.items) ? cart.items : [];
  lines.forEach(function (line) {
    if (!line || line.variant_id == null) return;
    var key = String(line.variant_id);
    if (!map[key]) map[key] = { line: line, quantity: 0 };
    map[key].quantity += line.quantity || 0;
  });
  return map;
}

// Diferença entre dois estados do carrinho: o que entrou (preço do estado novo)
// e o que saiu (preço do estado anterior).
function diffCart(prevCart, nextCart) {
  var prev = aggregateByVariant(prevCart);
  var next = aggregateByVariant(nextCart);
  var added = [];
  var removed = [];
  Object.keys(next).forEach(function (key) {
    var delta = next[key].quantity - (prev[key] ? prev[key].quantity : 0);
    if (delta > 0) added.push(buildCartLineItem(next[key].line, delta));
  });
  Object.keys(prev).forEach(function (key) {
    var delta = prev[key].quantity - (next[key] ? next[key].quantity : 0);
    if (delta > 0) removed.push(buildCartLineItem(prev[key].line, delta));
  });
  return { added: added, removed: removed };
}

function buildCartChangeEvents(prevCart, nextCart, currency) {
  var diff = diffCart(prevCart, nextCart);
  var events = [];
  if (diff.added.length) {
    events.push({ event: "add_to_cart", ecommerce: { currency: currency, value: sumItemsValue(diff.added), items: diff.added } });
  }
  if (diff.removed.length) {
    events.push({ event: "remove_from_cart", ecommerce: { currency: currency, value: sumItemsValue(diff.removed), items: diff.removed } });
  }
  return events;
}

// Formato do content_id enviado à Meta: igual ao que o app Facebook & Instagram
// usa no catálogo ("variant_id" = padrão, "product_id" ou "shopify_catalog").
function metaContentId(product, variant, format, country) {
  if (format === "shopify_catalog") return "shopify_" + (country || "BR") + "_" + product.id + "_" + variant.id;
  if (format === "product_id") return String(product.id);
  return String(variant.id);
}

// content_type que acompanha o formato: IDs de produto são "grupos de produto"
// no catálogo da Meta (é o que o app Facebook & Instagram envia).
function metaContentType(format) {
  return format === "product_id" ? "product_group" : "product";
}

function buildEngagementEvent(seconds, pageData, variantId, eventId, options) {
  var def = ENGAGEMENT_EVENTS[seconds];
  var product = pageData && pageData.product;
  var variant = selectedVariant(product, variantId);
  if (!def || !product || !variant) return null;
  options = options || {};
  var item = buildItem(product, variant, { quantity: 1 });
  return {
    event: def.ga4,
    event_id: eventId,
    engagement_seconds: seconds,
    ecommerce: { currency: pageData.currency, value: item.price, items: [item] },
    meta: {
      event_name: def.meta,
      custom_data: {
        content_ids: [metaContentId(product, variant, options.contentIdFormat, pageData.country)],
        content_type: metaContentType(options.contentIdFormat),
        content_name: product.title || "",
        value: item.price,
        currency: pageData.currency
      }
    }
  };
}

// UUID v4 a partir de uma função que devolve N bytes aleatórios.
function uuidV4(randomBytes) {
  var bytes = randomBytes(16);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  var hex = [];
  for (var i = 0; i < 16; i++) hex.push((bytes[i] + 0x100).toString(16).slice(1));
  return hex.slice(0, 4).join("") + "-" + hex.slice(4, 6).join("") + "-" + hex.slice(6, 8).join("") + "-" + hex.slice(8, 10).join("") + "-" + hex.slice(10).join("");
}
// </mapping>

// <engagement>
function createEngagementTimer(options) {
  var thresholds = options.thresholdsMs.slice().sort(function (a, b) { return a - b; });
  var fired = {};
  var accumulated = 0;
  var visibleSince = null;
  var timer = null;

  function clearTimer() {
    if (timer !== null) {
      options.clearTimeoutFn(timer);
      timer = null;
    }
  }

  function elapsedMs() {
    return accumulated + (visibleSince === null ? 0 : options.now() - visibleSince);
  }

  function check() {
    timer = null;
    if (visibleSince === null) return;
    var elapsed = elapsedMs();
    for (var i = 0; i < thresholds.length; i++) {
      var threshold = thresholds[i];
      if (fired[threshold]) continue;
      if (elapsed >= threshold) {
        fired[threshold] = true;
        try {
          options.onThreshold(threshold);
        } catch (err) {
          // um erro no envio não pode travar o próximo limiar.
        }
        continue;
      }
      timer = options.setTimeoutFn(check, threshold - elapsed);
      return;
    }
  }

  function sync() {
    var visible = options.getVisibilityState() === "visible";
    if (visible && visibleSince === null) {
      visibleSince = options.now();
      check();
    } else if (!visible && visibleSince !== null) {
      accumulated += options.now() - visibleSince;
      visibleSince = null;
      clearTimer();
    }
  }

  return { start: sync, onVisibilityChange: sync, elapsedMs: elapsedMs };
}
// </engagement>

// <cart-observer>
function isCartMutationUrl(url) {
  return /\/cart\/(add|change|update|clear)(\.js)?([?#]|$)/.test(String(url || ""));
}

// Endereço de uma chamada fetch: texto, objeto URL (href) ou Request (url).
function fetchInputUrl(input) {
  if (typeof input === "string") return input;
  if (input && typeof input.href === "string") return input.href;
  return (input && input.url) || "";
}

function createCartObserver(options) {
  var snapshot = options.initialCart || null;
  var chain = Promise.resolve();

  // Lê o carrinho, sempre em série; com `notify`, compara com o estado anterior.
  function read(notify) {
    chain = chain.then(function () {
      return options.fetchCart().then(function (next) {
        if (!next) return;
        var prev = snapshot;
        snapshot = next;
        if (notify && prev) {
          try {
            options.onChange(prev, next);
          } catch (err) {
            // erro de quem consome (montagem do add_to_cart/remove_from_cart):
            // não pode travar as próximas leituras; deixa um sinal no console.
            if (typeof console !== "undefined" && console && typeof console.warn === "function") {
              console.warn("[avelore-datalayer] falha ao processar alteração do carrinho");
            }
          }
        }
      });
    }).catch(function () {
      // falha de rede/JSON (fetchCart ou JSON.parse): mantém o estado anterior e segue.
    });
    return chain;
  }

  // Carrinho alterado nesta página: entrega (anterior, novo) para onChange.
  function refresh() {
    return read(true);
  }

  // Só troca a referência pelo carrinho atual, sem onChange (ex.: página
  // restaurada do bfcache - o carrinho pode ter mudado em outra página).
  function resync() {
    return read(false);
  }

  function install(win) {
    if (typeof win.fetch === "function") {
      var originalFetch = win.fetch;
      win.fetch = function (input) {
        var url = fetchInputUrl(input);
        var result = originalFetch.apply(win, arguments);
        if (isCartMutationUrl(url) && result && typeof result.then === "function") {
          result.then(function (res) {
            if (res && res.ok) refresh();
          }, function () {});
        }
        return result;
      };
    }
    var XHR = win.XMLHttpRequest;
    if (XHR && XHR.prototype) {
      var originalOpen = XHR.prototype.open;
      var originalSend = XHR.prototype.send;
      XHR.prototype.open = function (method, url) {
        this.__aveloreCartUrl = url;
        return originalOpen.apply(this, arguments);
      };
      XHR.prototype.send = function () {
        var xhr = this;
        if (isCartMutationUrl(xhr.__aveloreCartUrl)) {
          xhr.addEventListener("load", function () {
            if (xhr.status >= 200 && xhr.status < 300) refresh();
          });
        }
        return originalSend.apply(this, arguments);
      };
    }
  }

  return { install: install, refresh: refresh, resync: resync, getSnapshot: function () { return snapshot; } };
}
// </cart-observer>

function push(payload) {
  if (!payload) return;
  win.dataLayer = win.dataLayer || [];
  if (payload.ecommerce) win.dataLayer.push({ ecommerce: null });
  win.dataLayer.push(payload);
}

function readPageData() {
  var el = doc.getElementById("avelore-page-data");
  if (!el) return null;
  try {
    return JSON.parse(el.textContent);
  } catch (err) {
    return null;
  }
}

function randomBytes(n) {
  var bytes = new Uint8Array(n);
  if (win.crypto && typeof win.crypto.getRandomValues === "function") {
    win.crypto.getRandomValues(bytes);
  } else {
    for (var i = 0; i < n; i++) bytes[i] = Math.floor(Math.random() * 256);
  }
  return bytes;
}

function now() {
  return win.performance && typeof win.performance.now === "function" ? win.performance.now() : new Date().getTime();
}

function currentVariantId() {
  return variantIdFromSearch(win.location && win.location.search);
}

var data = readPageData();
if (!data) return;

try {
  if (data.page_type === "product") push(buildViewItemEvent(data, currentVariantId()));
  if (data.page_type === "collection" || data.page_type === "search") push(buildViewItemListEvent(data));
  if (data.page_type === "cart") push(buildViewCartEvent(data));
} catch (err) {
  // um JSON inesperado não pode quebrar a página.
}

if (data.page_type === "product" && data.product) {
  var engagementTimer = createEngagementTimer({
    thresholdsMs: ENGAGEMENT_THRESHOLDS_MS,
    now: now,
    getVisibilityState: function () { return doc.visibilityState; },
    setTimeoutFn: function (fn, ms) { return win.setTimeout(fn, ms); },
    clearTimeoutFn: function (id) { win.clearTimeout(id); },
    onThreshold: function (ms) {
      push(buildEngagementEvent(ms / 1000, data, currentVariantId(), uuidV4(randomBytes), { contentIdFormat: META_CONTENT_ID_FORMAT }));
    }
  });
  doc.addEventListener("visibilitychange", engagementTimer.onVisibilityChange);
  engagementTimer.start();
}

if (typeof win.fetch === "function") {
  var originalFetch = win.fetch;
  var cartUrl = ((win.Shopify && win.Shopify.routes && win.Shopify.routes.root) || "/") + "cart.js";
  var cartObserver = createCartObserver({
    initialCart: data.cart,
    fetchCart: function () {
      return originalFetch.call(win, cartUrl, { credentials: "same-origin", headers: { Accept: "application/json" } }).then(function (res) {
        return res && res.ok ? res.json() : null;
      });
    },
    onChange: function (prev, next) {
      buildCartChangeEvents(prev, next, (next && next.currency) || data.currency).forEach(push);
    }
  });
  cartObserver.install(win);
  // Página restaurada do cache de voltar/avançar (bfcache): o carrinho pode ter
  // mudado em outra página. Relê a referência sem gerar eventos; senão a próxima
  // alteração repetiria o que já foi medido lá.
  if (typeof win.addEventListener === "function") {
    win.addEventListener("pageshow", function (event) {
      if (event && event.persisted === true) cartObserver.resync();
    });
  }
}
})(window, document);
